import type {
  CreativeChallengeActivity,
  DrawingActivity,
  GuidePrimitive,
  GuideStep,
  GuidedDrawingActivity,
  NormalizedGuidePoint,
} from "./activityTypes";
import { validateActivityCatalog } from "./activityValidation";

const point = (x: number, y: number): NormalizedGuidePoint => ({ x, y });
const line = (x1: number, y1: number, x2: number, y2: number): GuidePrimitive => ({
  type: "line",
  start: point(x1, y1),
  end: point(x2, y2),
});
const ellipse = (cx: number, cy: number, rx: number, ry: number): GuidePrimitive => ({
  type: "ellipse",
  cx,
  cy,
  rx,
  ry,
});
const rectangle = (
  x: number,
  y: number,
  width: number,
  height: number,
  cornerRadius = 0,
): GuidePrimitive => ({
  type: "rectangle",
  x,
  y,
  width,
  height,
  ...(cornerRadius > 0 ? { cornerRadius } : {}),
});
const polyline = (points: readonly NormalizedGuidePoint[], closed = false): GuidePrimitive => ({
  type: "polyline",
  points,
  ...(closed ? { closed: true } : {}),
});
const arc = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): GuidePrimitive => ({ type: "arc", cx, cy, radius, startAngle, endAngle });
const bezier = (
  start: NormalizedGuidePoint,
  control1: NormalizedGuidePoint,
  control2: NormalizedGuidePoint,
  end: NormalizedGuidePoint,
): GuidePrimitive => ({ type: "bezier", start, control1, control2, end });

function step(id: string, instruction: string, primitives: readonly GuidePrimitive[]): GuideStep {
  return { id, instruction, primitives };
}

function guided(
  input: Omit<GuidedDrawingActivity, "type" | "previewPrimitives"> & {
    steps: readonly GuideStep[];
  },
): GuidedDrawingActivity {
  return {
    ...input,
    type: "guided",
    previewPrimitives: input.steps.flatMap((item) => item.primitives).slice(0, 12),
  };
}

function challenge(
  input: Omit<CreativeChallengeActivity, "type" | "encouragement">,
): CreativeChallengeActivity {
  return {
    ...input,
    type: "challenge",
    encouragement: "There is no wrong answer. Make it wonderfully yours!",
  };
}

const RAW_ACTIVITY_CATALOG: readonly DrawingActivity[] = [
  guided({
    id: "happy-cat",
    order: 1,
    title: "Happy Cat",
    description: "Build a cheerful cat from soft ovals and simple details.",
    difficulty: "Easy",
    steps: [
      step("body", "Draw one big oval for the cat's body.", [ellipse(0.5, 0.6, 0.23, 0.27)]),
      step("head", "Add a round head above the body.", [ellipse(0.5, 0.34, 0.19, 0.17)]),
      step("ears", "Give your cat two triangle ears.", [
        polyline([point(0.37, 0.24), point(0.39, 0.1), point(0.47, 0.2)], true),
        polyline([point(0.53, 0.2), point(0.61, 0.1), point(0.63, 0.24)], true),
      ]),
      step("face", "Add two eyes and a tiny nose.", [
        ellipse(0.44, 0.33, 0.018, 0.025),
        ellipse(0.56, 0.33, 0.018, 0.025),
        ellipse(0.5, 0.39, 0.025, 0.018),
      ]),
      step("whiskers", "Draw whiskers stretching out from each cheek.", [
        line(0.47, 0.4, 0.31, 0.37),
        line(0.47, 0.42, 0.3, 0.43),
        line(0.53, 0.4, 0.69, 0.37),
        line(0.53, 0.42, 0.7, 0.43),
      ]),
      step("tail", "Finish with a swishy tail and a smile.", [
        bezier(point(0.7, 0.68), point(0.9, 0.7), point(0.88, 0.42), point(0.75, 0.5)),
        arc(0.5, 0.39, 0.07, 0.25, Math.PI - 0.25),
      ]),
    ],
  }),
  guided({
    id: "friendly-fish",
    order: 2,
    title: "Friendly Fish",
    description: "Make a smiling fish ready for an underwater adventure.",
    difficulty: "Easy",
    steps: [
      step("body", "Start with a wide oval for the fish.", [ellipse(0.5, 0.52, 0.28, 0.18)]),
      step("tail", "Add a triangle tail behind it.", [
        polyline([point(0.24, 0.52), point(0.08, 0.34), point(0.08, 0.7)], true),
      ]),
      step("eye", "Give your fish one bright eye.", [
        ellipse(0.61, 0.47, 0.035, 0.04),
        ellipse(0.62, 0.47, 0.012, 0.014),
      ]),
      step("fins", "Draw a top fin and a little side fin.", [
        polyline([point(0.42, 0.35), point(0.5, 0.2), point(0.57, 0.35)], true),
        bezier(point(0.48, 0.54), point(0.56, 0.48), point(0.59, 0.62), point(0.49, 0.63)),
      ]),
      step("smile", "Add a friendly curved smile.", [arc(0.61, 0.53, 0.08, 0.2, 1.65)]),
      step("bubbles", "Finish with bubbles floating upward.", [
        ellipse(0.78, 0.31, 0.04, 0.04),
        ellipse(0.84, 0.21, 0.025, 0.025),
        ellipse(0.75, 0.16, 0.018, 0.018),
      ]),
    ],
  }),
  guided({
    id: "space-rocket",
    order: 3,
    title: "Space Rocket",
    description: "Launch a bold rocket with windows, fins and a fiery trail.",
    difficulty: "Medium",
    steps: [
      step("body", "Draw the rocket's tall main body.", [
        bezier(point(0.39, 0.68), point(0.36, 0.4), point(0.43, 0.18), point(0.5, 0.1)),
        bezier(point(0.5, 0.1), point(0.57, 0.18), point(0.64, 0.4), point(0.61, 0.68)),
        line(0.39, 0.68, 0.61, 0.68),
      ]),
      step("window", "Add a round window near the top.", [ellipse(0.5, 0.34, 0.075, 0.075)]),
      step("fins", "Give the rocket two strong side fins.", [
        polyline([point(0.39, 0.53), point(0.25, 0.72), point(0.4, 0.66)], true),
        polyline([point(0.61, 0.53), point(0.75, 0.72), point(0.6, 0.66)], true),
      ]),
      step("flame", "Draw a zig-zag flame below the rocket.", [
        polyline([
          point(0.42, 0.69),
          point(0.45, 0.89),
          point(0.5, 0.79),
          point(0.55, 0.89),
          point(0.58, 0.69),
        ]),
      ]),
      step("details", "Add two bands across the rocket.", [
        line(0.4, 0.47, 0.6, 0.47),
        line(0.4, 0.58, 0.6, 0.58),
      ]),
      step("stars", "Finish with sparkling stars around it.", [
        line(0.2, 0.24, 0.2, 0.34),
        line(0.15, 0.29, 0.25, 0.29),
        line(0.8, 0.18, 0.8, 0.3),
        line(0.74, 0.24, 0.86, 0.24),
      ]),
    ],
  }),
  guided({
    id: "sunny-house",
    order: 4,
    title: "Sunny House",
    description: "Create a cosy home with windows, a roof and sunshine.",
    difficulty: "Easy",
    steps: [
      step("walls", "Draw a big rectangle for the house.", [rectangle(0.25, 0.42, 0.5, 0.4)]),
      step("roof", "Add a triangle roof on top.", [
        polyline([point(0.2, 0.43), point(0.5, 0.16), point(0.8, 0.43)], true),
      ]),
      step("door", "Draw a front door with a round handle.", [
        rectangle(0.44, 0.61, 0.14, 0.21, 0.02),
        ellipse(0.55, 0.72, 0.012, 0.012),
      ]),
      step("windows", "Add a window on each side.", [
        rectangle(0.3, 0.52, 0.1, 0.1),
        rectangle(0.62, 0.52, 0.1, 0.1),
      ]),
      step("sun", "Finish with a bright sun in the sky.", [
        ellipse(0.82, 0.17, 0.07, 0.07),
        line(0.82, 0.06, 0.82, 0.02),
        line(0.82, 0.28, 0.82, 0.32),
        line(0.71, 0.17, 0.67, 0.17),
        line(0.93, 0.17, 0.97, 0.17),
      ]),
    ],
  }),
  guided({
    id: "cute-robot",
    order: 5,
    title: "Cute Robot",
    description: "Assemble a boxy robot friend with a happy face.",
    difficulty: "Easy",
    steps: [
      step("head", "Start with a rounded rectangle head.", [
        rectangle(0.34, 0.2, 0.32, 0.24, 0.04),
      ]),
      step("body", "Add a bigger box for the robot's body.", [
        rectangle(0.3, 0.47, 0.4, 0.3, 0.04),
      ]),
      step("face", "Give your robot two eyes and a smile.", [
        ellipse(0.43, 0.3, 0.025, 0.03),
        ellipse(0.57, 0.3, 0.025, 0.03),
        arc(0.5, 0.32, 0.08, 0.35, Math.PI - 0.35),
      ]),
      step("arms", "Draw an arm on each side.", [
        line(0.3, 0.55, 0.16, 0.68),
        line(0.7, 0.55, 0.84, 0.68),
      ]),
      step("legs", "Add two sturdy robot legs.", [
        line(0.4, 0.77, 0.38, 0.91),
        line(0.6, 0.77, 0.62, 0.91),
      ]),
      step("antenna", "Finish with an antenna and control buttons.", [
        line(0.5, 0.2, 0.5, 0.11),
        ellipse(0.5, 0.08, 0.025, 0.025),
        ellipse(0.43, 0.59, 0.018, 0.018),
        ellipse(0.5, 0.59, 0.018, 0.018),
        ellipse(0.57, 0.59, 0.018, 0.018),
      ]),
    ],
  }),
  guided({
    id: "smiling-dinosaur",
    order: 6,
    title: "Smiling Dinosaur",
    description: "Shape a gentle dinosaur with a long tail and playful spikes.",
    difficulty: "Medium",
    steps: [
      step("body", "Draw a large oval for the dinosaur's body.", [ellipse(0.5, 0.58, 0.27, 0.2)]),
      step("neck", "Curve a neck and small head upward.", [
        bezier(point(0.64, 0.48), point(0.73, 0.33), point(0.7, 0.2), point(0.78, 0.18)),
        ellipse(0.79, 0.18, 0.1, 0.07),
      ]),
      step("tail", "Add a long sweeping tail.", [
        bezier(point(0.25, 0.58), point(0.12, 0.5), point(0.08, 0.38), point(0.04, 0.4)),
      ]),
      step("legs", "Draw four simple legs below the body.", [
        line(0.35, 0.72, 0.32, 0.9),
        line(0.45, 0.75, 0.44, 0.91),
        line(0.57, 0.75, 0.56, 0.91),
        line(0.66, 0.71, 0.69, 0.88),
      ]),
      step("face", "Give your dinosaur an eye and a smile.", [
        ellipse(0.82, 0.16, 0.014, 0.018),
        arc(0.83, 0.18, 0.05, 0.35, 1.7),
      ]),
      step("spikes", "Add little triangle spikes along its back.", [
        polyline([
          point(0.32, 0.41),
          point(0.37, 0.32),
          point(0.41, 0.4),
          point(0.47, 0.29),
          point(0.51, 0.39),
          point(0.57, 0.3),
          point(0.61, 0.41),
        ]),
      ]),
      step("spots", "Finish with a few cheerful spots.", [
        ellipse(0.4, 0.57, 0.035, 0.03),
        ellipse(0.51, 0.63, 0.045, 0.035),
        ellipse(0.6, 0.53, 0.03, 0.025),
      ]),
    ],
  }),
  challenge({
    id: "robot-pet",
    order: 7,
    title: "Robot Pet",
    description: "Combine animal ideas with buttons, wheels and shiny parts.",
    difficulty: "Easy",
    prompt: "Design a robot pet.",
    previewPrimitives: [
      rectangle(0.27, 0.33, 0.46, 0.34, 0.06),
      ellipse(0.4, 0.48, 0.04, 0.04),
      ellipse(0.6, 0.48, 0.04, 0.04),
      line(0.5, 0.33, 0.5, 0.22),
      ellipse(0.5, 0.19, 0.025, 0.025),
    ],
  }),
  challenge({
    id: "undersea-house",
    order: 8,
    title: "Undersea House",
    description: "Imagine a cosy home surrounded by fish and bubbles.",
    difficulty: "Easy",
    prompt: "Draw a house under the sea.",
    previewPrimitives: [
      rectangle(0.3, 0.45, 0.4, 0.32),
      polyline([point(0.25, 0.45), point(0.5, 0.23), point(0.75, 0.45)], true),
      ellipse(0.8, 0.28, 0.04, 0.04),
      ellipse(0.87, 0.18, 0.025, 0.025),
    ],
  }),
  challenge({
    id: "flying-school-bus",
    order: 9,
    title: "Flying School Bus",
    description: "Give a school bus a surprising way to soar.",
    difficulty: "Medium",
    prompt: "Invent a flying school bus.",
    previewPrimitives: [
      rectangle(0.2, 0.4, 0.6, 0.27, 0.04),
      ellipse(0.35, 0.7, 0.06, 0.06),
      ellipse(0.67, 0.7, 0.06, 0.06),
      polyline([point(0.2, 0.47), point(0.06, 0.34), point(0.2, 0.58)], true),
      polyline([point(0.8, 0.47), point(0.94, 0.34), point(0.8, 0.58)], true),
    ],
  }),
  challenge({
    id: "jungle-picnic",
    order: 10,
    title: "Jungle Picnic",
    description: "Gather jungle friends for a meal in the leaves.",
    difficulty: "Easy",
    prompt: "Draw a jungle picnic.",
    previewPrimitives: [
      polyline([point(0.25, 0.72), point(0.5, 0.42), point(0.75, 0.72)], true),
      line(0.5, 0.42, 0.5, 0.78),
      ellipse(0.25, 0.27, 0.12, 0.08),
      ellipse(0.75, 0.27, 0.12, 0.08),
    ],
  }),
  challenge({
    id: "moon-playground",
    order: 11,
    title: "Moon Playground",
    description: "Create slides and swings for low-gravity fun.",
    difficulty: "Medium",
    prompt: "Design a playground on the moon.",
    previewPrimitives: [
      arc(0.5, 0.68, 0.25, Math.PI, Math.PI * 2),
      line(0.26, 0.68, 0.26, 0.82),
      line(0.74, 0.68, 0.74, 0.82),
      ellipse(0.78, 0.24, 0.11, 0.11),
      ellipse(0.75, 0.21, 0.025, 0.025),
    ],
  }),
  challenge({
    id: "friendly-monster",
    order: 12,
    title: "Friendly Monster",
    description: "Mix silly features into a monster who loves making friends.",
    difficulty: "Easy",
    prompt: "Draw a friendly monster.",
    previewPrimitives: [
      ellipse(0.5, 0.52, 0.25, 0.28),
      ellipse(0.42, 0.45, 0.04, 0.05),
      ellipse(0.58, 0.45, 0.04, 0.05),
      arc(0.5, 0.53, 0.1, 0.25, Math.PI - 0.25),
    ],
  }),
  challenge({
    id: "vegetable-superhero",
    order: 13,
    title: "Vegetable Superhero",
    description: "Turn a favourite vegetable into a brave hero.",
    difficulty: "Medium",
    prompt: "Invent a vegetable superhero.",
    previewPrimitives: [
      ellipse(0.5, 0.45, 0.15, 0.25),
      polyline([point(0.42, 0.2), point(0.5, 0.08), point(0.58, 0.2)]),
      polyline([point(0.38, 0.48), point(0.16, 0.34), point(0.3, 0.7)], true),
      line(0.44, 0.72, 0.38, 0.9),
      line(0.56, 0.72, 0.62, 0.9),
    ],
  }),
  challenge({
    id: "tiny-bedroom-dragon",
    order: 14,
    title: "Tiny Bedroom Dragon",
    description: "Hide a miniature dragon among everyday bedroom things.",
    difficulty: "Medium",
    prompt: "Draw a tiny dragon in your room.",
    previewPrimitives: [
      rectangle(0.16, 0.48, 0.68, 0.32),
      ellipse(0.52, 0.5, 0.14, 0.09),
      bezier(point(0.4, 0.52), point(0.28, 0.43), point(0.27, 0.65), point(0.18, 0.6)),
      polyline([point(0.5, 0.43), point(0.58, 0.29), point(0.63, 0.47)], true),
    ],
  }),
  challenge({
    id: "rainy-day-cat",
    order: 15,
    title: "Rainy-Day Cat",
    description: "Show a cat finding fun while raindrops fall.",
    difficulty: "Easy",
    prompt: "Draw a cat enjoying a rainy day.",
    previewPrimitives: [
      arc(0.5, 0.43, 0.25, Math.PI, Math.PI * 2),
      line(0.5, 0.43, 0.5, 0.78),
      ellipse(0.5, 0.62, 0.12, 0.15),
      line(0.22, 0.22, 0.18, 0.32),
      line(0.78, 0.22, 0.74, 0.32),
    ],
  }),
  challenge({
    id: "space-garden",
    order: 16,
    title: "Space Garden",
    description: "Grow unusual plants among planets and stars.",
    difficulty: "Medium",
    prompt: "Design a garden in space.",
    previewPrimitives: [
      arc(0.5, 0.74, 0.22, Math.PI, Math.PI * 2),
      line(0.42, 0.7, 0.42, 0.4),
      line(0.58, 0.7, 0.58, 0.34),
      ellipse(0.38, 0.39, 0.07, 0.04),
      ellipse(0.63, 0.33, 0.07, 0.04),
      ellipse(0.8, 0.19, 0.09, 0.09),
    ],
  }),
  challenge({
    id: "ice-cream-castle",
    order: 17,
    title: "Ice-Cream Castle",
    description: "Build delicious towers, scoops and wafer walls.",
    difficulty: "Easy",
    prompt: "Draw an ice-cream castle.",
    previewPrimitives: [
      rectangle(0.25, 0.44, 0.5, 0.38),
      rectangle(0.2, 0.35, 0.16, 0.47),
      rectangle(0.64, 0.35, 0.16, 0.47),
      ellipse(0.28, 0.3, 0.09, 0.09),
      ellipse(0.72, 0.3, 0.09, 0.09),
      ellipse(0.5, 0.39, 0.1, 0.1),
    ],
  }),
  challenge({
    id: "future-school",
    order: 18,
    title: "Future School",
    description: "Imagine classrooms and playgrounds from tomorrow.",
    difficulty: "Medium",
    prompt: "Invent a school from the future.",
    previewPrimitives: [
      rectangle(0.2, 0.38, 0.6, 0.42, 0.04),
      ellipse(0.5, 0.38, 0.18, 0.12),
      rectangle(0.44, 0.62, 0.12, 0.18),
      line(0.28, 0.49, 0.72, 0.49),
      line(0.28, 0.58, 0.72, 0.58),
    ],
  }),
];

/** Every bundled activity is runtime-validated before any route or renderer can access it. */
export const ACTIVITY_CATALOG = validateActivityCatalog(RAW_ACTIVITY_CATALOG);
export const GUIDED_ACTIVITIES = ACTIVITY_CATALOG.filter(
  (activity): activity is GuidedDrawingActivity => activity.type === "guided",
);
export const CREATIVE_CHALLENGES = ACTIVITY_CATALOG.filter(
  (activity): activity is CreativeChallengeActivity => activity.type === "challenge",
);

export function getActivityById(id: string): DrawingActivity | undefined {
  return ACTIVITY_CATALOG.find((activity) => activity.id === id);
}

export function activityProjectName(activity: DrawingActivity): string {
  return activity.type === "guided" ? `${activity.title} Drawing` : `${activity.title} Challenge`;
}
